import React, { useState, useRef, useEffect, useCallback } from "react";
import "./ColorPicker.css";

/* ── Colour math ────────────────────────────── */
function hexToRgb(hex) {
  const h = (hex || "").replace("#", "");
  const n = h.length === 3 ? h.split("").map(c => c + c).join("") : h.padEnd(6, "0");
  return {
    r: parseInt(n.slice(0, 2), 16) || 0,
    g: parseInt(n.slice(2, 4), 16) || 0,
    b: parseInt(n.slice(4, 6), 16) || 0,
  };
}
function rgbToHex({ r, g, b }) {
  const toHex = n => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function rgbToHsv({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}
function hsvToRgb({ h, s, v }) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}
const hexToHsv = hex => rgbToHsv(hexToRgb(hex));
const hsvToHex = hsv => rgbToHex(hsvToRgb(hsv));

const DEFAULT_PRESETS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#6366f1",
  "#ec4899", "#e11d48", "#d946ef", "#8b5cf6", "#06b6d4", "#059669", "#84cc16",
];
const STORAGE_KEY = "cp_saved_colors";

/* ── Component ──────────────────────────────── */
export default function ColorPicker({ value, onChange, compact = false }) {
  const safeValue = value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000";
  const [open, setOpen] = useState(false);
  const [popPos, setPopPos] = useState(null); // { top, left } in viewport coords
  const [hsv, setHsv] = useState(() => hexToHsv(safeValue));
  const [hexInput, setHexInput] = useState(safeValue.replace("#", ""));
  const [saved, setSaved] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
    catch { return []; }
  });
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Compute a clamped position so the popover never overflows the
  // enclosing form container (prevents it from bleeding into adjacent
  // columns or the live-preview area). Recomputed on open / resize / scroll.
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const place = () => {
      const t = triggerRef.current.getBoundingClientRect();
      const POPOVER_W = 312; // 280 content + 16px padding × 2
      const POPOVER_H = 430;
      const MARGIN = 8;

      // Prefer the enclosing form / settings panel as the right boundary;
      // fall back to the viewport edge. This keeps the popover inside the
      // column the trigger lives in (or at least the form) rather than
      // sticking into the live-preview card.
      const form = triggerRef.current.closest(".form-section")
        || triggerRef.current.closest(".config-panel")
        || triggerRef.current.closest(".dc-design-form")
        || triggerRef.current.closest("form");
      const containerR = form ? form.getBoundingClientRect().right : window.innerWidth;
      const viewportR  = window.innerWidth - MARGIN;
      const rightBound = Math.min(containerR - MARGIN, viewportR);
      const viewportB  = window.innerHeight - MARGIN;

      // Try: trigger-aligned → right-aligned-to-trigger → right-aligned-to-container.
      let left = t.left;
      if (left + POPOVER_W > rightBound) left = t.right - POPOVER_W;
      if (left + POPOVER_W > rightBound) left = rightBound - POPOVER_W;
      if (left < MARGIN) left = MARGIN;

      let top = t.bottom + 8;
      if (top + POPOVER_H > viewportB && t.top - POPOVER_H - 8 > MARGIN) {
        top = t.top - POPOVER_H - 8;
      }
      setPopPos({ top, left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Sync with external value (compare against last-seen so internal edits don't trigger)
  const lastSyncedRef = useRef(safeValue);
  useEffect(() => {
    if (!value || value === lastSyncedRef.current) return;
    lastSyncedRef.current = value;
    const next = /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000";
    setHsv(hexToHsv(next));
    setHexInput(next.replace("#", ""));
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const commit = useCallback(nextHsv => {
    setHsv(nextHsv);
    const hex = hsvToHex(nextHsv);
    setHexInput(hex.replace("#", ""));
    onChange(hex);
  }, [onChange]);

  const commitHex = useCallback(hex => {
    setHsv(hexToHsv(hex));
    setHexInput(hex.replace("#", ""));
    onChange(hex);
  }, [onChange]);

  function startDrag(updater) {
    return e => {
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      updater(e.clientX, e.clientY, rect);
      function onMove(ev) { updater(ev.clientX, ev.clientY, rect); }
      function onUp() {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      }
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    };
  }

  const handleSvDown = startDrag((x, y, rect) => {
    const s = Math.max(0, Math.min(1, (x - rect.left) / rect.width));
    const v = Math.max(0, Math.min(1, 1 - (y - rect.top) / rect.height));
    commit({ ...hsv, s, v });
  });

  const handleHueDown = startDrag((x, _y, rect) => {
    const h = Math.max(0, Math.min(360, ((x - rect.left) / rect.width) * 360));
    commit({ ...hsv, h });
  });

  function handleHexChange(raw) {
    const v = raw.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    setHexInput(v);
    if (/^[0-9a-fA-F]{6}$/.test(v)) commitHex("#" + v);
  }

  function addSavedColor() {
    const hex = hsvToHex(hsv).toLowerCase();
    if (saved.includes(hex)) return;
    const next = [hex, ...saved].slice(0, 14);
    setSaved(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  }

  function removeSavedColor(hex) {
    const next = saved.filter(c => c.toLowerCase() !== hex.toLowerCase());
    setSaved(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  }

  async function pickWithEyedropper() {
    if (!window.EyeDropper) return;
    try {
      const ed = new window.EyeDropper();
      const r = await ed.open();
      if (r?.sRGBHex) commitHex(r.sRGBHex);
    } catch { /* user cancelled */ }
  }

  const hueColor = hsvToHex({ h: hsv.h, s: 1, v: 1 });
  const currentHex = hsvToHex(hsv);
  const savedLower = saved.map(c => c.toLowerCase());
  const defaults = DEFAULT_PRESETS.filter(p => !savedLower.includes(p.toLowerCase()));
  const hasEyedropper = typeof window !== "undefined" && !!window.EyeDropper;

  return (
    <div className={`colour-picker ${open ? "open" : ""}`}>
      <button
        type="button"
        ref={triggerRef}
        className={`colour-picker-trigger ${compact ? "compact" : ""}`}
        onClick={() => setOpen(v => !v)}
        aria-label="Choose colour"
        aria-expanded={open}
      >
        <span className="cp-trigger-swatch" style={{ background: currentHex }} />
        {!compact && (
          <>
            <span className="cp-trigger-value">{currentHex.toUpperCase()}</span>
            <svg className="cp-trigger-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </>
        )}
      </button>
      {open && popPos && (
        <div
          ref={popoverRef}
          className="colour-picker-popover"
          style={{ top: popPos.top, left: popPos.left }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div
            className="cp-sv-canvas"
            style={{ background: `linear-gradient(to top, #000, rgba(0,0,0,0)), linear-gradient(to right, #fff, ${hueColor})` }}
            onMouseDown={handleSvDown}
          >
            <div
              className="cp-sv-thumb"
              style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: currentHex }}
            />
          </div>

          <div className="cp-hue-track" onMouseDown={handleHueDown}>
            <div className="cp-hue-thumb" style={{ left: `${(hsv.h / 360) * 100}%`, background: hueColor }} />
          </div>

          <div className="cp-current-row">
            <span className="cp-current-swatch" style={{ background: currentHex }} />
            <div className="cp-hex-field">
              <span className="cp-input-hash">#</span>
              <input
                type="text"
                className="cp-hex-input"
                value={hexInput.toUpperCase()}
                onChange={e => handleHexChange(e.target.value)}
                maxLength={6}
                spellCheck={false}
              />
            </div>
            {hasEyedropper && (
              <button
                type="button"
                className="cp-icon-btn"
                onClick={pickWithEyedropper}
                title="Pick colour from screen"
                aria-label="Pick colour from screen"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m2 22 1-1h3l9-9"/><path d="M3 21v-3l9-9"/><path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3l.4.4Z"/>
                </svg>
              </button>
            )}
          </div>

          <div className="cp-palette">
            <div className="cp-palette-header">
              <span className="cp-palette-label">Palette</span>
              <button
                type="button"
                className="cp-icon-btn"
                onClick={addSavedColor}
                title="Save current colour"
                aria-label="Save current colour"
                style={{ width: 26, height: 26 }}
                disabled={savedLower.includes(currentHex.toLowerCase())}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
            </div>
            <div className="cp-presets">
              {saved.map((c, i) => (
                <span key={`saved-${c}-${i}`} className="cp-preset-wrap">
                  <button
                    type="button"
                    className={`cp-preset ${c.toLowerCase() === currentHex.toLowerCase() ? "active" : ""}`}
                    style={{ background: c }}
                    onClick={() => commitHex(c)}
                    aria-label={c}
                  />
                  <button
                    type="button"
                    className="cp-preset-remove"
                    onClick={() => removeSavedColor(c)}
                    title="Remove"
                    aria-label={`Remove ${c}`}
                  >×</button>
                </span>
              ))}
              {defaults.map((c, i) => (
                <span key={`default-${c}-${i}`} className="cp-preset-wrap">
                  <button
                    type="button"
                    className={`cp-preset ${c.toLowerCase() === currentHex.toLowerCase() ? "active" : ""}`}
                    style={{ background: c }}
                    onClick={() => commitHex(c)}
                    aria-label={c}
                  />
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
